import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmail, 
  registerWithEmail, 
  signOutUser, 
  resetPassword,
  changePassword as firebaseChangePassword,
  signInWithGoogle,
  createUserDocument,
  getUserDocument,
  onAuthStateChange,
  upgradeToPro,
  sendVerificationEmail
} from '../lib/firebase';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  // Listen for auth state changes - this is the single source of truth
  useEffect(() => {
    // If running inside an iframe, disable Firebase Auth state listener to avoid token refresh race conditions with the parent window
    if (window.self !== window.top) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        // User is signed in - set user immediately
        setCurrentUser(user);
        
        // Unblock UI immediately so dashboard can start fetching its data
        // We don't wait for Firestore data to load
        setLoading(false);
        
        // Load Firestore data in background
        try {
          await createUserDocument(user); 
          const userDataResult = await getUserDocument(user.uid);
          setUserData(userDataResult);
        } catch (error) {
          // Handle offline/network errors gracefully
          if (error.code === 'unavailable' || error.message?.includes('offline')) {
            // Client is offline - will sync when connection returns
            // Don't log as error, this is expected behavior
          } else {
            console.error("Error fetching user data:", error);
          }
          setUserData(null);
        }
      } else {
        // User is signed out
        setCurrentUser(null);
        setUserData(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Sign in with email and password
  const login = async (email, password) => {
    setAuthLoading(true);
    try {
      // Attempt sign in
      const credential = await signInWithEmail(email, password);

      // Check email verification - if not verified, throw error but keep user signed in
      // so they can be redirected to verify-email page
      if (!credential.user.emailVerified) {
        const error = new Error('Email not verified');
        error.code = 'auth/email-not-verified';
        error.needsVerification = true;
        throw error;
      }

      // onAuthStateChanged will handle setting the user state if verified.
      return credential;
    } catch (error) {
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // Register with email and password
  const register = async (email, password, additionalData = {}) => {
    setAuthLoading(true);
    try {
      const result = await registerWithEmail(email, password);
      
      // We still call createUserDocument here to pass additionalData (like name)
      // on the very first creation. The listener will handle subsequent logins.
      await createUserDocument(result.user, additionalData);

      // Send email verification to the newly registered user
      // This is critical - must happen before returning
      try {
        console.log('Sending verification email to:', result.user.email);
        await sendVerificationEmail(result.user);
        console.log('Verification email sent successfully');
        toast.success('Verification email sent! Please check your inbox.');
      } catch (emailError) {
        console.error('Failed to send verification email:', emailError);
        // Show error but don't throw - user is still registered, they can resend from verify page
        toast.error('Could not send verification email. Please use the resend button.');
      }

      // Don't sign out - keep user signed in so they can access verify-email page
      // The ProtectedRoute will redirect to verify-email if not verified

      return result;
    } catch (error) {
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // Sign in with Google (popup method)
  const loginWithGoogle = async () => {
    setAuthLoading(true);
    try {
      const result = await signInWithGoogle();
      toast.success('Successfully signed in with Google!');
      // onAuthStateChanged will handle setting the user state
      return result;
    } catch (error) {
      setAuthLoading(false);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    setAuthLoading(true);
    try {
      await signOutUser();
      // onAuthStateChanged will handle clearing user state.
    } catch (error) {
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  // Reset password
  const forgotPassword = async (email) => {
    try {
      await resetPassword(email);
    } catch (error) {
      throw error;
    }
  };

  // Change password (from settings)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await firebaseChangePassword(currentPassword, newPassword);
    } catch (error) {
      throw error;
    }
  };

  // Upgrade to pro
  const upgradeUser = async () => {
    if (!currentUser) return;
    
    try {
      await upgradeToPro(currentUser.uid);
      const updatedUserData = await getUserDocument(currentUser.uid);
      setUserData(updatedUserData);
      toast.success('Successfully upgraded to Pro!');
    } catch (error) {
      throw error;
    }
  };

  // Check if user can create more CVs
  const canCreateCV = () => {
    if (!userData) return false;
    if (userData.plan === 'pro') return true;
    return (userData.cvCount || 0) < (userData.maxCVs || 3);
  };

  const value = {
    currentUser,
    userData,
    loading,
    authLoading,
    login,
    register,
    loginWithGoogle,
    logout,
    forgotPassword,
    changePassword,
    upgradeUser,
    canCreateCV
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 